// ============================================================================
// @alloyhead/core — Render Cache
// ============================================================================
// Provides reusable OffscreenCanvas instances and a cached checkerboard
// pattern generator. At 4K, allocating a new OffscreenCanvas per layer
// per frame creates ~33MB of GC pressure per canvas. This cache eliminates
// that by reusing canvases across frames.
//
// Usage:
//   const cache = new RenderCache();
//   const { canvas, ctx } = cache.getCanvas('group', 3840, 2160);
//   ctx.clearRect(0, 0, 3840, 2160);
//   // ... draw into ctx ...
//   cache.getCheckerboard(3840, 2160); // returns cached pattern
// ============================================================================
/**
 * Pool of reusable OffscreenCanvas instances keyed by purpose.
 * Canvases are resized (not reallocated) when dimensions change.
 */
export class RenderCache {
    canvases = new Map();
    checkerboard = null;
    checkerW = 0;
    checkerH = 0;
    checkerTile = null;
    /**
     * Get a reusable OffscreenCanvas for the given purpose and dimensions.
     * The canvas is created on first use and resized if dimensions change.
     * The caller is responsible for clearing it before drawing.
     */
    getCanvas(key, w, h) {
        let entry = this.canvases.get(key);
        if (!entry || entry.w !== w || entry.h !== h) {
            const canvas = entry?.canvas || new OffscreenCanvas(w, h);
            if (canvas.width !== w)
                canvas.width = w;
            if (canvas.height !== h)
                canvas.height = h;
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            entry = { canvas, ctx, w, h };
            this.canvases.set(key, entry);
        }
        return { canvas: entry.canvas, ctx: entry.ctx };
    }
    /**
     * Get a cached checkerboard pattern matching the given dimensions.
     * PERF: Uses a tiny 16x16 tile + createPattern instead of 65,000 individual
     * fillRect calls. The pattern is GPU-composited via a single fillRect.
     * At 4K this reduces checkerboard generation from ~8ms to <0.1ms.
     */
    getCheckerboard(w, h, cellSize = 8, colorA = '#1a1a1a', colorB = '#222') {
        if (this.checkerboard && this.checkerW === w && this.checkerH === h) {
            return this.checkerboard;
        }
        // Create tiny tile (2x2 cells)
        const tileSize = cellSize * 2;
        if (!this.checkerTile) {
            this.checkerTile = new OffscreenCanvas(tileSize, tileSize);
        }
        if (this.checkerTile.width !== tileSize)
            this.checkerTile.width = tileSize;
        if (this.checkerTile.height !== tileSize)
            this.checkerTile.height = tileSize;
        const tx = this.checkerTile.getContext('2d');
        tx.fillStyle = colorA;
        tx.fillRect(0, 0, tileSize, tileSize);
        tx.fillStyle = colorB;
        tx.fillRect(0, 0, cellSize, cellSize);
        tx.fillRect(cellSize, cellSize, cellSize, cellSize);
        // Fill full-size canvas using pattern (GPU-accelerated tiling)
        const bg = this.checkerboard || new OffscreenCanvas(w, h);
        if (bg.width !== w)
            bg.width = w;
        if (bg.height !== h)
            bg.height = h;
        const bx = bg.getContext('2d');
        const pat = bx.createPattern(this.checkerTile, 'repeat');
        bx.fillStyle = pat;
        bx.fillRect(0, 0, w, h);
        this.checkerboard = bg;
        this.checkerW = w;
        this.checkerH = h;
        return bg;
    }
    /**
     * Release all cached canvases. Call on destroy.
     */
    clear() {
        this.canvases.clear();
        this.checkerboard = null;
        this.checkerTile = null;
        this.checkerW = 0;
        this.checkerH = 0;
    }
}
//# sourceMappingURL=render-cache.js.map